// src/admin/PageContentEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * Props
 *  - baseURL   (string)  API root, e.g. https://rankridgeschools.com/rankridgeschool/dev/v1
 *  - apiKey    (string)  X-API-Key header value
 *  - initial   (object)  existing row or prefilled blank
 *  - onClose   (fn)      called when modal closed
 *  - onSaved   (fn)      called after successful save
 */
export default function PageContentEditor({
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
  apiKey = "12345678",
  initial = {
    page_content_id: null,
    page_id: null,
    subpage_id: null,
    h1_main_title: "",
    carousel_heading: "",
    subdescription: "",
    status: "active",
  },
  onClose,
  onSaved,
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [baseURL, apiKey]
  );

  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // dropdown data
  const [pages, setPages] = useState([]);
  const [subpages, setSubpages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingSubpages, setLoadingSubpages] = useState(false);

  // load pages on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPages(true);
        const { data } = await api.get("/pages", {
          params: { status: "active", sort: "page_id", order: "asc", limit: 1000 },
        });
        if (!cancelled) setPages(data?.items || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || "Failed to load pages");
      } finally {
        if (!cancelled) setLoadingPages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  // load subpages whenever page_id changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!form.page_id) {
        setSubpages([]);
        setForm((f) => ({ ...f, subpage_id: null }));
        return;
      }
      try {
        setLoadingSubpages(true);
        const { data } = await api.get("/subpages", {
          params: {
            page_id: form.page_id,
            status: "active",
            sort: "subpage_id",
            order: "asc",
            limit: 1000,
          },
        });
        if (!cancelled) setSubpages(data?.items || []);
        // if current subpage_id no longer belongs to this page, clear it
        if (!cancelled && form.subpage_id) {
          const ok = (data?.items || []).some(
            (s) => Number(s.subpage_id) === Number(form.subpage_id)
          );
          if (!ok) setForm((f) => ({ ...f, subpage_id: null }));
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || "Failed to load subpages");
      } finally {
        if (!cancelled) setLoadingSubpages(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, form.page_id]);

  // close on Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]); // eslint-disable-line

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.h1_main_title.trim()) return "H1 Main Title is required.";
    // Optional: require at least one of page_id or subpage_id
    if (!form.page_id && !form.subpage_id)
      return "Select a Page or a Subpage.";
    return "";
  };

  const save = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        page_id: form.page_id ?? null,
        subpage_id: form.subpage_id ?? null,
        h1_main_title: form.h1_main_title,
        carousel_heading: form.carousel_heading || "",
        subdescription: form.subdescription || "",
        status: form.status || "active",
      };
      if (form.page_content_id) {
        await api.put(`/page_content/${form.page_content_id}`, {
          page_content_id: form.page_content_id,
          ...payload,
        });
      } else {
        await api.post(`/page_content`, payload);
      }
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold">
            {form.page_content_id ? "Edit Content" : "Add Content"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-4 grid gap-4">
          {error && (
            <div className="px-3 py-2 text-sm rounded bg-red-50 text-red-700 border border-red-200">
              {error}
            </div>
          )}

          {/* Page/Subpage pickers */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">Page</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.page_id ?? ""}
                onChange={(e) =>
                  upd("page_id", e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">— Select Page —</option>
                {loadingPages ? (
                  <option disabled>Loading…</option>
                ) : (
                  pages.map((p) => (
                    <option key={p.page_id} value={p.page_id}>
                      [{p.page_id}] {p.page_name} — {p.page_slug}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-700">Subpage (optional)</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.subpage_id ?? ""}
                onChange={(e) =>
                  upd(
                    "subpage_id",
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                disabled={!form.page_id || loadingSubpages}
              >
                <option value="">
                  {form.page_id
                    ? loadingSubpages
                      ? "Loading…"
                      : "— Select Subpage —"
                    : "Select a Page first"}
                </option>
                {subpages.map((sp) => (
                  <option key={sp.subpage_id} value={sp.subpage_id}>
                    [{sp.subpage_id}] {sp.subpage_name} — {sp.subpage_slug}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Main fields */}
          <div>
            <label className="text-sm text-gray-700">
              H1 Main Title <span className="text-red-600">*</span>
            </label>
            <input
              className="border rounded px-3 py-2 w-full"
              value={form.h1_main_title}
              onChange={(e) => upd("h1_main_title", e.target.value)}
              placeholder="Main SEO H1 title for this page/subpage"
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">Carousel Heading</label>
            <input
              className="border rounded px-3 py-2 w-full"
              value={form.carousel_heading || ""}
              onChange={(e) => upd("carousel_heading", e.target.value)}
              placeholder="Optional: heading for a carousel component"
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">Subdescription</label>
            <textarea
              rows={8}
              className="border rounded px-3 py-2 w-full"
              value={form.subdescription || ""}
              onChange={(e) => upd("subdescription", e.target.value)}
              placeholder="Short paragraph shown under the H1"
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">Status</label>
            <select
              className="border rounded px-3 py-2 w-full"
              value={form.status || "active"}
              onChange={(e) => upd("status", e.target.value)}
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
              <option value="deleted">deleted</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded bg-green-700 text-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
